/**
 * Who is signed in — hydrated from `GET /v1/admin/me`, never decoded from the token.
 *
 * The access token carries a `scopes` claim, and reading it here would work, right up until the
 * day the backend changes how a scope is expanded at mint time. `docs/05-admin/02` §1 is explicit:
 * **the SPA builds its menu from the `/v1/admin/me` response, never by decoding the JWT itself.**
 * One source for identity, and it is the one the server also uses to answer.
 *
 * The boot path is the part worth reading twice. After a reload there is **no access token** — it
 * only ever lived in memory (`tokens.ts`) — so `bootstrap()` cannot start by calling `me`. It
 * starts by spending the refresh token, and only then asks who it is. Three outcomes, all of them
 * ordinary:
 *
 *   - no refresh token at all → signed out, go to sign-in;
 *   - a refresh token the server refuses → signed out, and `authed.ts` has already cleared it;
 *   - a refresh that works → an access token, then `me`, then the console.
 *
 * `totp_enrolment_status` comes back on `me` as well, and it is not decoration: an admin who is
 * not `enrolled` may go to enrolment and nowhere else (§2.1).
 */
import { createAuthedClient, type SignOutReason } from '../api/authed';
import { apiFetch } from '../api/client';
import { tokenStore, hasStoredSession } from './tokens';
import { isScope, type Scope } from '../rbac';
import type { components } from '../../api/generated/schema';

/** The generated shape. Never hand-write this — it is the contract, not a convenience. */
export type AdminMe = components['schemas']['AdminMeOut'];

export interface AdminSession {
  id: number;
  email: string;
  name: string;
  /** Only the scopes this console knows how to shape itself around (R9's seven). */
  scopes: readonly Scope[];
  /** `enrolled` | `pending` | `reset_required` — anything but `enrolled` means enrolment first. */
  totpStatus: string;
}

export const ENROLLED = 'enrolled';

export function sessionFrom(me: AdminMe): AdminSession {
  return {
    id: me.id,
    email: me.email,
    name: me.full_name,
    /* An unrecognised scope string is dropped rather than carried: the console cannot shape a nav
       around a grant it has no modules for, and passing it through would produce a scope chip
       naming something nobody can act on. The server still enforces whatever it granted. */
    scopes: me.scopes.filter(isScope),
    totpStatus: me.totp_enrolment_status,
  };
}

/** Signed out is a value, not an absence — `reason` is what the sign-in screen explains. */
export type AuthState =
  | { status: 'unknown' }
  | { status: 'signed-in'; session: AdminSession }
  | { status: 'enrolment-required'; session: AdminSession }
  | { status: 'signed-out'; reason: SignOutReason | null };

let state: AuthState = { status: 'unknown' };
const listeners = new Set<() => void>();

function set(next: AuthState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function authState(): AuthState {
  return state;
}

export function subscribeToAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The signed-in admin, or null. Sugar over `authState()` for the many callers that only want this. */
export function currentAdmin(): AdminSession | null {
  return state.status === 'signed-in' || state.status === 'enrolment-required' ? state.session : null;
}

export const authedClient = createAuthedClient({
  tokens: tokenStore,
  onSignedOut: (reason) => set({ status: 'signed-out', reason }),
});

/** Fetches `me` and files it under the right status. Shared by bootstrap and by sign-in. */
export async function adoptSession(): Promise<AuthState> {
  const me = await authedClient.request<AdminMe>('/v1/admin/me');
  const session = sessionFrom(me);
  const next: AuthState =
    session.totpStatus === ENROLLED
      ? { status: 'signed-in', session }
      : { status: 'enrolment-required', session };
  set(next);
  return next;
}

/**
 * Called once at start-up. Spends the refresh token, then asks who it belongs to.
 *
 * Never throws: every failure here is a signed-out console, which is a screen, not an error.
 */
export async function bootstrap(): Promise<AuthState> {
  if (!hasStoredSession()) {
    set({ status: 'signed-out', reason: null });
    return state;
  }

  const renewed = await authedClient.refresh();
  /* `refresh()` has already set signed-out and cleared the tokens if the server refused. */
  if (!renewed) {
    if (state.status !== 'signed-out') set({ status: 'signed-out', reason: 'expired' });
    return state;
  }

  try {
    return await adoptSession();
  } catch {
    /* A refresh that worked followed by a `me` that did not is a backend problem, not a session
       one — but there is nothing useful to render either way. */
    set({ status: 'signed-out', reason: 'expired' });
    return state;
  }
}

/**
 * A logout anywhere logs out everywhere (§1). Each tab holds its own session — the refresh token
 * lives in that tab's sessionStorage — so the tab that signs out says so, and every other tab
 * ends and revokes its own. Absent in a browser without the API: one tab then signs out alone,
 * which is what it did before this existed.
 */
export const AUTH_CHANNEL = 'bookmypandiit-admin-auth';
export const LOGGED_OUT = 'logged_out';
const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(AUTH_CHANNEL);
channel?.addEventListener('message', (event: MessageEvent) => {
  /* No echo back: this tab ends its own session and does not broadcast again. */
  if (event.data === LOGGED_OUT && state.status !== 'signed-out') void endSession();
});

/** Ends the session here and, best-effort, on the server. Never throws. */
export async function signOut(): Promise<void> {
  channel?.postMessage(LOGGED_OUT);
  await endSession();
}

async function endSession(): Promise<void> {
  const pair = tokenStore.read();
  tokenStore.clear();
  set({ status: 'signed-out', reason: null });
  if (!pair?.refresh) return;
  try {
    /* Fired with the captured tokens rather than through the authed client, because the local
       session is deliberately already gone — the operator should not watch a spinner to leave.
       Both halves are supplied: the bearer is what the endpoint authenticates on, and the refresh
       token in the body lets the server kill it now instead of when it is next presented.
       Best-effort by design — the endpoint is idempotent, and a failed revoke must not strand
       anyone on a console they have logically left. */
    await apiFetch('/v1/auth/logout', {
      method: 'POST',
      headers: pair.access ? { Authorization: `Bearer ${pair.access}` } : {},
      body: { refresh_token: pair.refresh },
    });
  } catch {
    /* Nothing to do and nothing to say: the refresh token expires on its own. */
  }
}

/** For tests, and for the sign-in screen, which mints a session rather than restoring one. */
export function setAuthState(next: AuthState): void {
  set(next);
}
